// components/SelfPaced/StudentCatalogPage.js
// Student-facing course catalog — themed, responsive, dark-mode ready
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  Search, BookOpen, Clock, BarChart3, ChevronRight,
  Sparkles, GraduationCap, Users, Trophy, Play, BookMarked,
} from 'lucide-react';
import { AppButton, AppBadge, EmptyState, SearchInput, FilterBar, FilterSelect } from '../ui';
import { listPublicCourses, getMyEnrollments } from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'price-low', label: 'Price: Low → High' },
  { value: 'price-high', label: 'Price: High → Low' },
];

/* ── Course Card ────────────────────────────────────────────── */
const CatalogCourseCard = ({ course, owned, onClick }) => {
  const levelClass = (course.level || 'beginner').toLowerCase();
  const hasDiscount = course.discountPrice && course.discountPrice < course.price;
  const isFree = !course.price || course.price === 0;
  const discountPct = hasDiscount
    ? Math.round((1 - course.discountPrice / course.price) * 100)
    : 0;

  return (
    <div className="spc-card" onClick={onClick} role="button" tabIndex={0}>
      <div className="spc-card__thumb">
        {(course.thumbnailUrl || course.thumbnail) ? (
          <img src={course.thumbnailUrl || course.thumbnail} alt={course.title || course.name} />
        ) : (
          <div className="spc-card__thumb-placeholder">
            <BookMarked size={32} />
          </div>
        )}
        <span className={`spc-card__level spc-card__level--${levelClass}`}>
          {course.level || 'Beginner'}
        </span>
      </div>

      <div className="spc-card__body">
        {course.category && (
          <span className="spc-card__category">{course.category}</span>
        )}
        <h4 className="spc-card__title">{course.title || course.name}</h4>
        {course.description && (
          <p className="spc-card__desc">
            {course.description.length > 100
              ? course.description.slice(0, 100) + '…'
              : course.description}
          </p>
        )}
        <div className="spc-card__meta">
          {course.duration && <span><Clock size={13} /> {course.duration}</span>}
          {course.language && <span><BookOpen size={13} /> {course.language}</span>}
          {course.level && <span><BarChart3 size={13} /> {course.level}</span>}
        </div>
      </div>

      <div className="spc-card__footer">
        <div className="spc-card__pricing">
          {owned ? (
            <AppBadge color="primary" size="sm">✓ Owned</AppBadge>
          ) : isFree ? (
            <span className="spc-card__price spc-card__price--free">Free</span>
          ) : hasDiscount ? (
            <>
              <span className="spc-card__price">₹{course.discountPrice?.toLocaleString('en-IN')}</span>
              <span className="spc-card__price-original">₹{course.price?.toLocaleString('en-IN')}</span>
              <AppBadge color="emerald" size="sm">{discountPct}% off</AppBadge>
            </>
          ) : (
            <span className="spc-card__price">₹{course.price?.toLocaleString('en-IN')}</span>
          )}
        </div>
        <span className="spc-card__action">{owned ? 'Continue' : 'View'} <ChevronRight size={14} /></span>
      </div>
    </div>
  );
};

/* ── Main Page ──────────────────────────────────────────────── */
export default function StudentCatalogPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('');
  const [sort, setSort] = useState('newest');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [coursesRes, enrollRes] = await Promise.allSettled([
        listPublicCourses(),
        getMyEnrollments(),
      ]);
      setCourses(coursesRes.status === 'fulfilled' ? (coursesRes.value.data?.data || coursesRes.value.data || []) : []);
      const enrollments = enrollRes.status === 'fulfilled' ? (enrollRes.value.data?.data || enrollRes.value.data || []) : [];
      setEnrolledIds(new Set(enrollments.filter(e => ['active', 'completed'].includes(e.status)).map(e => e.course?._id || e.course)));
    } catch { setCourses([]); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = [...courses];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.category || '').toLowerCase().includes(q)
      );
    }
    if (level) list = list.filter(c => (c.level || '').toLowerCase() === level);
    if (sort === 'newest') list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (sort === 'popular') list.sort((a, b) => (b.enrollmentCount || 0) - (a.enrollmentCount || 0));
    if (sort === 'price-low') list.sort((a, b) => (a.discountPrice || a.price || 0) - (b.discountPrice || b.price || 0));
    if (sort === 'price-high') list.sort((a, b) => (b.discountPrice || b.price || 0) - (a.discountPrice || a.price || 0));
    return list;
  }, [courses, search, level, sort]);

  return (
    <div className="sp-page">
      {/* Hero */}
      <div className="spc-hero">
        <div className="spc-hero__content">
          <AppBadge variant="secondary" color="primary">
            <Sparkles size={12} /> Learning Marketplace
          </AppBadge>
          <h1 className="spc-hero__title">
            Expand your skills with <span className="spc-hero__gradient">self-paced courses</span>
          </h1>
          <p className="spc-hero__subtitle">
            Learn at your own pace with expert-crafted courses. Start today and build the skills you need.
          </p>
          <div className="spc-hero__stats">
            <span><BookOpen size={14} /> {courses.length} Courses</span>
            <span><Users size={14} /> Self-Paced</span>
            <span><Trophy size={14} /> Certificates</span>
          </div>
        </div>
        <div className="spc-hero__art">
          <div className="spc-hero__float spc-hero__float--1"><GraduationCap size={18} /> Learn Anywhere</div>
          <div className="spc-hero__float spc-hero__float--2"><Play size={18} /> Video Lessons</div>
          <div className="spc-hero__float spc-hero__float--3"><Trophy size={18} /> Get Certified</div>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <SearchInput placeholder="Search courses..." value={search} onChange={setSearch} />
            <FilterSelect value={sort} onChange={setSort} options={SORT_OPTIONS} />
          </div>
        }
      >
        <FilterSelect
          value={level}
          onChange={setLevel}
          options={[
            { value: '', label: 'All Levels' },
            { value: 'beginner', label: 'Beginner' },
            { value: 'intermediate', label: 'Intermediate' },
            { value: 'advanced', label: 'Advanced' },
          ]}
        />
        <AppBadge variant="secondary">{filtered.length} course{filtered.length !== 1 ? 's' : ''}</AppBadge>
      </FilterBar>

      {/* Content */}
      {loading ? (
        <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Search size={28} />} title="No courses found" description="Try adjusting your search or filters." />
      ) : (
        <div className="spc-grid">{filtered.map(c => (
          <CatalogCourseCard key={c._id} course={c} owned={enrolledIds.has(c._id)} onClick={() => navigate(`/s/self-paced/${c._id}`)} />
        ))}</div>
      )}
    </div>
  );
}
